import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {isMobile} from '#core/utils/context-utils.js';
import {merge, ensureHierarchy, mergeTo} from '#core/utils/config-utils.js';
import {buildIntentsFromConfig} from '#core/utils/intent-factory.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';
import {generateLinkMenuVerticalList} from '#core/application/modules/tiles/list-item/index.js';
import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';
import {generateSettingMenuGlobalStuff} from '#core/application/modules/tiles/settings/index.js';

import {MenusLinksLists} from '../menus-links-list.js';

import SCHEMA from './az-menu.schema.yaml';
import PALETTE_SCHEMA from './az-menu.palette-schema.yaml';

class AZMenuFeature extends AbstractVisualFeature {
    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    get configSchema() {
        return SCHEMA;
    }

    beforeMojitoConfigBuild(mojitoConfig) {
        const configurations = ensureHierarchy(mojitoConfig, 'services.configurations');

        mergeTo(configurations, {
            SportsMenu: {
                numberOfTopSportsToShow: this.config.numberOfTopSports,
            },
        });

        return super.beforeMojitoConfigBuild(mojitoConfig);
    }

    getDesktopConfig(PALETTE) {
        const {FONT, DESKTOP_AZ_SPORTS_LIST} = PALETTE;

        const ITEMS_SEPARATOR_STYLE = DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR
            ? {
                  borderWidth: '0 0 1px 0',
                  borderColor: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SEPARATOR_COLOR,
                  borderStyle: 'solid',
              }
            : undefined;
        const DESKTOP_FAVORITE_ICON_STYLE = icon(PALETTE.DESKTOP_AZ_FAVORITE_ICON_COLOR);

        return {
            // Used for both desktop and mobile. And there could be totally different styles
            SportsMenuView: {
                separator: {
                    show: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR,
                },
                container: {
                    style: {
                        ...resolveBackground(PALETTE.DESKTOP_AZ_CONTAINER_BG),
                        ...(PALETTE.DESKTOP_AZ_SHOW_SHADOW ? {boxShadow: PALETTE.DESKTOP_AZ_SHADOW} : {}),

                        borderRadius: PALETTE.DESKTOP_AZ_CONTAINER_BORDER_RADIUS,

                        ...(PALETTE.DESKTOP_AZ_BORDER_WIDTH && PALETTE.DESKTOP_AZ_BORDER_COLOR !== 'none'
                            ? {
                                  borderWidth: PALETTE.DESKTOP_AZ_BORDER_WIDTH,
                                  borderColor: PALETTE.DESKTOP_AZ_BORDER_COLOR,
                                  borderStyle: 'solid',
                              }
                            : undefined),
                        overflow: 'hidden', // needed for round corners
                    },
                },
                style: {
                    header: {
                        ...resolveBackground(PALETTE.DESKTOP_AZ_HEADER_BG),
                        icon: icon(PALETTE.DESKTOP_AZ_HEADER_ICON_COLOR),
                        height: 40,
                        ...(PALETTE.DESKTOP_AZ_SHOW_HEADER_UNDERLINE
                            ? {
                                  borderWidth: '0 0 1px 0',
                                  borderColor: PALETTE.DESKTOP_AZ_HEADER_UNDERLINE_COLOR,
                                  borderStyle: 'solid',
                              }
                            : undefined),
                    },
                    headerText: PALETTE.DESKTOP_AZ_HEADER_TEXT,

                    separator: {
                        height: 0,
                        margin: 0,
                    },
                    toggleButton: {
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.DESKTOP_AZ_EXPAND_BUTTON.TERTIARY_BUTTON_TEXT,
                    },
                },
                favoriteIcon: DESKTOP_FAVORITE_ICON_STYLE,
                favoriteIconToggled: DESKTOP_FAVORITE_ICON_STYLE,
                sportItem: {
                    style: {
                        base: {
                            ...ITEMS_SEPARATOR_STYLE,
                            _mobile_margin: '0 8px',
                            _mobile_padding: '0 0 0 8px',
                            /**
                             * Setting `min-width: 0;` below is the hack to make `display: flex;` work with the `white-space: nowrap;`.
                             * Details: https://stackoverflow.com/a/38224283
                             */
                            minWidth: '0',
                            ...DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_TEXT,
                        },
                    },
                },
                itemIcon: {
                    style: {
                        fill: DESKTOP_AZ_SPORTS_LIST.LIST_ITEM_ICON_COLOR,
                        minWidth: '24px',
                    },
                },
                itemContainer: {
                    truncate: true,
                    container: {
                        flexBasis: 'auto',
                    },
                },
                rotation: {
                    image: {
                        style: {
                            fill: PALETTE.DESKTOP_AZ_EXPAND_BUTTON.TERTIARY_BUTTON_ICON_COLOR,
                            minHeight: '8px',
                            // minWidth: '8px' - by default
                        },
                    },
                },
            },
        };
    }

    getMobileConfig(PALETTE) {
        const {FONT, MOBILE_AZ_SPORTS_LIST} = PALETTE;

        const ITEMS_SEPARATOR_STYLE = MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR
            ? {
                  borderWidth: '0 0 1px 0',
                  borderColor: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SEPARATOR_COLOR,
                  borderStyle: 'solid',
              }
            : undefined;
        const MOBILE_FAVORITE_ICON_STYLE = icon(PALETTE.MOBILE_AZ_FAVORITE_ICON_COLOR);

        return {
            // Used for both desktop and mobile. And there could be totally different styles
            SportsMenuView: {
                separator: {
                    show: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_SHOW_SEPARATOR,
                },
                style: {
                    separator: {
                        height: 0,
                        margin: 0,
                    },
                },
                favoriteIcon: MOBILE_FAVORITE_ICON_STYLE,
                favoriteIconButton: MOBILE_FAVORITE_ICON_STYLE,
                favoriteIconToggled: MOBILE_FAVORITE_ICON_STYLE,
                sportItem: {
                    style: {
                        base: {
                            ...ITEMS_SEPARATOR_STYLE,
                            _mobile_margin: '0 8px',
                            _mobile_padding: '0 0 0 8px',
                            /**
                             * Setting `min-width: 0;` below is the hack to make `display: flex;` work with the `white-space: nowrap;`.
                             * Details: https://stackoverflow.com/a/38224283
                             */
                            minWidth: '0',
                            ...MOBILE_AZ_SPORTS_LIST.LIST_ITEM_TEXT,
                        },
                    },
                },
                itemIcon: {
                    style: {
                        fill: MOBILE_AZ_SPORTS_LIST.LIST_ITEM_ICON_COLOR,
                        minWidth: '24px',
                    },
                },
                itemContainer: {
                    truncate: true,
                    container: {
                        flexBasis: 'auto',
                    },
                },
            },

            AppOverlay: {
                slidingAZMenu: {
                    slidingPane: {
                        style: {
                            childrenContainer: {
                                ...resolveBackground(PALETTE.MOBILE_AZ_CONTAINER_BG),
                            },
                        },
                        closeButtonContainer: {
                            style: {
                                right: 'calc(100% + 4px)',
                            },
                        },
                        closeButton: generateCloseButton(PALETTE.MOBILE_AZ_CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
                    },
                },
            },

            // In mojito, it is not possible to pass all settings menu configs to mobile AZMenu.
            // The only way to make it global
            ...generateSettingMenuGlobalStuff(PALETTE.MOBILE_AZ_SETTINGS),

            AZMenu: {
                settingsMenu: {
                    container: {
                        style: {
                            padding: '8px',
                        },
                    },
                },
                sequentialStateBackButtonLabel: {
                    style: {
                        fontSize: FONT.SIZE_MEDIUM,
                        ...PALETTE.MOBILE_AZ_BACK_BUTTON_TEXT,
                    },
                },
                sequentialStateBackButtonIcon: icon(PALETTE.MOBILE_AZ_BACK_BUTTON_ICON_COLOR),
            },
            AzOverview: {
                container: {
                    nthSeparator: {
                        0: {
                            style: {color: 'transparent'}, // fix mojito default style
                        },
                    },
                    style: {
                        // Actually background is not needed here, as it always sits inside AppOverlay->slidingAZMenu->azMenu
                        // But leaving it here to show consistent mock-ups
                        ...resolveBackground(PALETTE.MOBILE_AZ_CONTAINER_BG),
                    },

                    separator: {
                        style: {
                            color: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                            size: PALETTE.MOBILE_AZ_SHOW_SECTION_SEPARATOR ? 1 : 0,
                        },
                    },
                },
                sectionName: {
                    container: {
                        padding: '8px 8px 8px 16px', // override mojito's wrong config
                        ...resolveBackground(PALETTE.MOBILE_AZ_SECTION_HEADER_CONTAINER_BG),
                        ...(PALETTE.MOBILE_AZ_SECTION_NAME_UNDERLINE
                            ? {
                                  //border: `1px solid ${PALETTE.DIVIDER_COLOR}`, <-- this not works (mojito magic)
                                  borderStyle: 'solid',
                                  borderWidth: '0 0 1px',
                                  borderColor: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                              }
                            : {}),
                    },
                    style: {
                        textTransform: 'uppercase',
                        fontWeight: FONT.WEIGHT_BOLD,
                        ...PALETTE.MOBILE_AZ_SECTION_HEADER_TEXT,
                    },
                },
                sportsSectionAllSportsButton: {
                    style: {
                        base: {
                            padding: '0 16px', // override mojito's wrong config
                            fontSize: FONT.SIZE_MEDIUM,
                            ...(PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON_OVERLINE
                                ? {
                                      borderStyle: 'solid',
                                      borderWidth: '1px 0 0',
                                      borderColor: PALETTE.MOBILE_AZ_SECTION_SEPARATOR_COLOR,
                                  }
                                : {}),
                            ...PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                        //PALETTE.ALL_SPORTS_BUTTON_TEXT || PALETTE.TERTIARY_BUTTON_TEXT
                    },
                },
                sportsSectionAllSportsButtonIcon: merge(
                    {
                        style: {
                            padding: 8, //fix position according to text line
                        },
                    },
                    icon(PALETTE.MOBILE_AZ_ALL_SPORTS_BUTTON.TERTIARY_BUTTON_ICON_COLOR)
                ),
                informationSectionContainer: merge(generateLinkMenuVerticalList(PALETTE.MOBILE_AZ_INFORMATION_LIST), {
                    regularItem: {
                        // selected: {
                        //     style: {
                        //         base: {
                        //             _mobile_margin: '0 8px',
                        //             padding: 8, // override mojito's wrong config
                        //             _mobile_padding: 8,
                        //         },
                        //     },
                        // },
                        unselected: {
                            style: {
                                base: {
                                    _mobile_margin: '0 8px',
                                    padding: 8, // override mojito's wrong config
                                    _mobile_padding: '0 0 0 8px',
                                },
                            },
                        },
                    },
                }),
                informationSectionItems: buildIntentsFromConfig(ApplicationConfig.config.mobileInfoSectionItems),

                moreSection: {
                    links: buildIntentsFromConfig(ApplicationConfig.config.mobileMoreSectionItems),
                    linkButtons: {
                        disableSelectedButton: false,
                        container: {
                            style: {
                                flexBasis: 'auto', // flexBasis 40 coming from FW is breaking the layout on Safari
                            },
                        },
                        selectableButton: {
                            unselected: {
                                style: {
                                    base: {
                                        margin: '0 8px',
                                        padding: '0 0 0 8px', // override mojito's default
                                        alignItems: 'center',
                                        ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                        ...ITEMS_SEPARATOR_STYLE,
                                    },
                                },
                            },
                            selected: {
                                style: {
                                    base: {
                                        margin: '0 8px',
                                        padding: '0 0 0 8px', // override mojito's default
                                        alignItems: 'center',
                                        ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                                    },
                                },
                            },
                        },
                    },
                    linkImage: {
                        style: {
                            width: 16,
                            height: 16,
                            fill: PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_ICON_COLOR,
                        },
                    },
                    linkLabel: {
                        style: {
                            ...PALETTE.MOBILE_AZ_MORE_LIST.LIST_ITEM_TEXT,
                        },
                    },
                },
            },
        };
    }

    getVisualConfig(PALETTE) {
        return isMobile() ? this.getMobileConfig(PALETTE) : this.getDesktopConfig(PALETTE);
    }
}

new AZMenuFeature(MenusLinksLists);
